// Shared constants for the diagnose flow.
// Extracted from route.ts so that both the legacy static path and the
// KB-backed enrichment can reuse them without duplication.

/**
 * Maps zone region names (from regional-climate.ts) to the vocabulary
 * used in disease/pest/weed commonRegions fields.
 */
export const REGION_MATCH_MAP: Record<string, string[]> = {
  'Northeast': ['Northeast'],
  'Mid-Atlantic': ['Mid-Atlantic'],
  'Southeast': ['Southeast', 'Gulf Coast'],
  'Great Lakes': ['Great Lakes'],
  'Upper Midwest': ['Upper Midwest'],
  'Central': ['Central'],
  'South Central': ['South Central', 'Gulf Coast', 'Southwest'],
  'Mountain West': ['Central', 'Southwest', 'Pacific Northwest'],
  'West Coast': ['Pacific Northwest', 'Pacific', 'Southwest'],
  // Fine-grained climate sub-regions added 2026-06-20. The KB commonRegions
  // vocabulary is the legacy/extended set, so map each new region onto its own
  // name plus its legacy parent's match terms — otherwise a Florida/California
  // user matches almost nothing and every diagnosis gets an "Uncommon" note.
  'Florida': ['Florida', 'Southeast', 'Gulf Coast'],
  'Desert Southwest': ['Desert Southwest', 'Southwest', 'South Central'],
  'California': ['California', 'West Coast', 'Pacific', 'Southwest'],
  'Pacific Northwest': ['Pacific Northwest', 'Pacific', 'West Coast'],
}

export function regionMatchesCommon(zoneName: string, commonRegions: string[]): boolean {
  if (commonRegions.includes('All regions')) return true
  const matchTerms = REGION_MATCH_MAP[zoneName] || [zoneName]
  return commonRegions.some(r => matchTerms.includes(r))
}

// Nutrient deficiency treatment data (inline — small dataset, not worth KB migration)
export const NUTRIENT_TREATMENTS: Record<string, {
  products: Array<{ name: string; type: string; applicationTiming: string }>
  cultural: string[]
  organic: string[]
  window: string
}> = {
  nitrogen: {
    products: [{ name: 'Milorganite 6-4-0', type: 'Organic Fertilizer', applicationTiming: 'Spring and fall, 4-6 week intervals' }],
    cultural: ['Apply 0.5-1 lb N per 1000 sq ft per application', 'Do not exceed 4 lb N per 1000 sq ft per year', 'Water in after application'],
    organic: ['Milorganite', 'Blood meal', 'Compost topdressing'],
    window: 'Spring, Fall'
  },
  iron: {
    products: [{ name: 'Ironite Mineral Supplement 1-0-1', type: 'Iron Supplement', applicationTiming: 'Spring through fall when grass is actively growing' }],
    cultural: ['Apply chelated iron at 2 oz per 1000 sq ft', 'Lower soil pH if above 7.0', 'Avoid over-application which can stain concrete'],
    organic: ['Chelated iron foliar spray', 'Sulfur to lower pH', 'Compost with iron-rich amendments'],
    window: 'Spring, Summer, Fall'
  },
  potassium: {
    products: [{ name: 'Muriate of Potash 0-0-60', type: 'Potassium Fertilizer', applicationTiming: 'Fall application preferred, split spring/fall' }],
    cultural: ['Apply 1-2 lb K2O per 1000 sq ft per year', 'Split into fall and spring applications', 'Soil test to confirm deficiency before applying'],
    organic: ['Greensand', 'Wood ash (raises pH)', 'Kelp meal'],
    window: 'Fall, Spring'
  },
  phosphorus: {
    products: [{ name: 'Triple Superphosphate 0-46-0', type: 'Phosphorus Fertilizer', applicationTiming: 'At seeding or in fall based on soil test' }],
    cultural: ['Apply only based on soil test results', 'Best applied during establishment', 'Many states restrict phosphorus application - check local regulations'],
    organic: ['Bone meal', 'Rock phosphate', 'Compost'],
    window: 'Fall, At seeding'
  },
  magnesium: {
    products: [{ name: 'Epsom Salt (Magnesium Sulfate)', type: 'Magnesium Supplement', applicationTiming: 'Spring or fall, 2-3 lb per 1000 sq ft' }],
    cultural: ['Apply Epsom salt at 2-3 lb per 1000 sq ft', 'If soil pH is low, use dolomitic lime instead', 'Foliar spray for quick correction'],
    organic: ['Dolomitic lime (also raises pH)', 'Epsom salt foliar spray', 'Compost'],
    window: 'Spring, Fall'
  }
}

// Environmental stress treatment data (cultural-only — no products needed)
export const STRESS_TREATMENTS: Record<string, { cultural: string[]; window: string }> = {
  drought: {
    cultural: ['Water deeply 1 inch per week, preferably in early morning', 'Raise mowing height by 0.5-1 inch', 'Avoid fertilizing during active drought stress', 'Limit foot traffic on stressed turf'],
    window: 'Immediate'
  },
  overwatering: {
    cultural: ['Reduce watering frequency - allow soil to dry between waterings', 'Improve drainage with core aeration', 'Check for and fix irrigation system issues', 'Watch for signs of fungal disease'],
    window: 'Immediate'
  },
  compaction: {
    cultural: ['Core aerate in the active growing season for your grass type', 'Topdress with compost after aeration', 'Redirect foot traffic with pathways', 'Overseed after aeration for recovery'],
    window: 'Spring (cool-season), Late spring (warm-season)'
  },
  shade: {
    cultural: ['Prune trees to increase light penetration', 'Consider shade-tolerant grass varieties (Fine Fescue, St. Augustine)', 'Raise mowing height in shaded areas', 'Reduce fertilizer and water in shaded zones'],
    window: 'Ongoing'
  },
  'chemical burn': {
    cultural: ['Flush area with heavy watering (1-2 inches)', 'Do not mow until recovery begins', 'Avoid further chemical applications to affected area', 'Reseed or resod if grass crowns are dead'],
    window: 'Immediate'
  },
  scalping: {
    cultural: ['Raise mowing height immediately', 'Water lightly and frequently to support recovery', 'Apply light fertilizer once new growth appears', 'Follow the 1/3 rule - never remove more than 1/3 of blade height'],
    window: 'Immediate'
  }
}
